import { Client } from "@/Common/Api/Client";
import { ClientResponse } from "@/Common/Api/ClientResponse";
import { Email } from "@/Common/Api/Contacts/Dto/Email";
import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { Individual } from "@/Common/Api/Individuals/Dto/Individual";
import { IRepresentantsDto } from "@/Common/Api/Patient/Dto/IRepresentantsDto";
import { LinkedContact } from "@/Common/Api/Patient/Dto/LinkedContact";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import ButtonGroup from "@/Common/Components/Buttons/ButtonGroup";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { TextboxMaskedControl } from "@/Common/Components/Inputs/TextboxMaskedControl";
import { Representant } from "@/Common/Components/Patient/PatientRepresentant";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { ContactPointType } from "@/Common/Models/Contacts/Enums/ContactPointType";
import { PhoneType } from "@/Common/Models/Contacts/Enums/PhoneType";
import { Gender } from "@/Common/Models/Patient/PatientEnums";
import { Person } from "@/Common/Models/Person/Person";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { RelativeApiClient } from "@/Relative/Api/RelativeApiClient";
import { Relative } from "@/Relative/Models/Relative";

type AddedContactPointIds = { EmailContactPointId?: string, PhoneContactPointId?: string };

class AddRepresentativeEmailPopupProps {
    public currentlyProcessing: boolean;
    public representantsDto: IRepresentantsDto;
    public enablePhoneContent: boolean;
    public patientUnderAgeWithoutRepresentantLabel: string;
    public patientWithoutRepresentantContactLabel: string;
    public onCancel: () => void;
    public onSubmit: (contactPoindsIds: string[]) => void;
    public onContinueWithoutContact: () => void;
    public errors: string;

    public static defaultProps: AddRepresentativeEmailPopupProps = {
        errors: "",
        currentlyProcessing: false,
        representantsDto: null,
        enablePhoneContent: false,
        patientUnderAgeWithoutRepresentantLabel: null,
        patientWithoutRepresentantContactLabel: null,
        onCancel: null,
        onSubmit: null,
        onContinueWithoutContact: null
    };
}

class AddRepresentativeEmailPopupState {
    public isEmailValid: boolean;
    public emailAddress: string;
    public isNotifiedByEmail: boolean;
    public errors: string[];
    public newRepresentantLastName: string;
    public newRepresentantFirstName: string;
    public newRelationshipTypeLookupId: string;
    public newRepresentantType: string;
    public representant?: Representant;
    public patientIsOfAgeOfConsent: boolean;
    public patientHasNoDateOfBirthOnFile: boolean;
    public patient?: Individual;
    public phone: PhoneNumber;
    public showPhoneField: boolean;

    public static getDefault(): AddRepresentativeEmailPopupState {
        return {
            isEmailValid: false,
            isNotifiedByEmail: false,
            emailAddress: "",
            errors: [],
            newRepresentantLastName: "",
            newRepresentantFirstName: "",
            newRelationshipTypeLookupId: "",
            newRepresentantType: "",
            patientIsOfAgeOfConsent: false,
            patientHasNoDateOfBirthOnFile: false,
            patient: null,
            phone: PhoneNumber.newPhone(ContactPointType.Personal, PhoneType.Mobile),
            showPhoneField: false
        };
    }
}

class AddRepresentativeEmailPopup extends BaseReactComponent<AddRepresentativeEmailPopupProps, AddRepresentativeEmailPopupState> {
    private emailFieldName: string = "EmailField";
    private representantFirstNameFieldName: string = "RepresentantFirstNameField";
    private phoneFieldId: string = "phoneFieldId";

    private get patientHasRepresentant(): boolean {
        return this.state.representant != null;
    }

    private get patientIsOfAgeOrHasNoDateOfBirth(): boolean {
        return (this.state.patientIsOfAgeOfConsent || this.state.patientHasNoDateOfBirthOnFile);
    }

    constructor(props: AddRepresentativeEmailPopupProps) {
        super(props);

        const initialState = AddRepresentativeEmailPopupState.getDefault();
        initialState.patient = props.representantsDto.Patient;
        initialState.representant = this.getRepresentant(props.representantsDto.Representants);
        initialState.patientHasNoDateOfBirthOnFile = Individual.hasNoDateOfBirthOnFile(props.representantsDto.Patient);
        initialState.patientIsOfAgeOfConsent = PatientHelper.getPatientIsOfAgeOfConsent(props.representantsDto.Patient.DateOfBirth);
        initialState.showPhoneField = props.enablePhoneContent && cacheStore.portalConfiguration?.HasSmsNotificationEnabled;

        this.state = initialState;
    }

    public componentDidMount(): void {
        this.setOverlay("AddRepresentantEmailPopupOverlay", { transparent: true });
        this.focusFirstField();
        this.setStartingRelationshipType();
    }

    public componentDidUpdate(prevProps: Readonly<AddRepresentativeEmailPopupProps>): void {
        if (this.props !== prevProps) {
            if (this.props.errors != prevProps.errors) {
                this.mutateState(s => { s.errors = [this.props.errors]; });
            }
        }
    }

    public componentWillUnmount(): void {
        overlayStore.hideOverlay("AddRepresentantEmailPopupOverlay");
    }

    public render(): JSX.Element {
        let width = 650;
        return (
            <div className="popup" style={{ left: "50%", width: width + "px", marginLeft: -width / 2, top: "20%" }}>

                <div className="p-header">
                    <h4>
                        {cacheStore.GetString("WarningLabel")}
                    </h4>
                </div>

                <div className="p-body" style={{ padding: "15px 25px 15px 25px" }}>
                    {this.getDisclaimerParagraphs().map((item, index) => {
                        let keyValue = `Paragraph_${index}`;
                        return (<p style={{ "marginBottom": "10px" }} key={keyValue}>{item}</p>);
                    })}

                    {!this.patientHasRepresentant && !this.state.patientIsOfAgeOfConsent && !this.state.patientHasNoDateOfBirthOnFile && (
                        <div className="row" style={{ "marginBottom": "10px" }}>
                            <div className="col-lg-2 col-md-2 col-sm-2 col-center-align">
                                <label className="form-label" style={{ "paddingTop": "6px" }}>{cacheStore.GetString("FirstNameLabel")}</label>
                            </div>

                            <div className="col-lg-10 col-md-10 col-sm-10">
                                <TextboxControl
                                    valid={!!this.state.newRepresentantFirstName}
                                    id={this.representantFirstNameFieldName}
                                    value={this.state.newRepresentantFirstName}
                                    onChange={value => this.mutateState(s => s.newRepresentantFirstName = value)}
                                />
                            </div>
                        </div>
                    )}

                    {!this.patientHasRepresentant
                        && !this.state.patientIsOfAgeOfConsent
                        && !this.state.patientHasNoDateOfBirthOnFile
                        && (
                            <div className="row" style={{ "marginBottom": "10px" }}>
                                <div className="col-lg-2 col-md-2 col-sm-2 col-center-align">
                                    <label className="form-label" style={{ "paddingTop": "6px" }}>{cacheStore.GetString("LastNameLabel")}</label>
                                </div>

                                <div className="col-lg-10 col-md-10 col-sm-10">
                                    <TextboxControl
                                        valid={!!this.state.newRepresentantLastName}
                                        id="RepresentantLastNameField"
                                        value={this.state.newRepresentantLastName}
                                        onChange={value => this.mutateState(s => s.newRepresentantLastName = value)}
                                    />
                                </div>
                            </div>
                        )
                    }

                    {!this.patientHasRepresentant && !this.state.patientIsOfAgeOfConsent && !this.state.patientHasNoDateOfBirthOnFile && (
                        <div className="row" style={{ "marginBottom": "10px" }}>
                            <div className="col-lg-2 col-md-2 col-sm-2 col-center-align">
                                <label className="form-label" style={{ "paddingTop": "6px" }}>{cacheStore.GetString("RelationshipLabel")}</label>
                            </div>

                            <div className="col-lg-10 col-md-10 col-sm-10">
                                <select onChange={e => this.onSelectRelationshipType(e)} className="form-control myle-control">
                                    {cacheStore.GetRelationshipLookupsTuples(this.getPatientGender()).map((p, idx) =>
                                        <option key={p[0].LookupID + "_" + idx} value={p[0].LookupCode}>{cacheStore.GetLocalizedValueByLookupId(p[0].LookupID)}</option>
                                    )}
                                </select>
                            </div>
                        </div>
                    )}

                    <div className="row" style={{ "marginBottom": "10px" }}>
                        <div className="col-lg-2 col-md-2 col-sm-2 col-center-align">
                            <label className="form-label" style={{ "paddingTop": "6px" }}>{cacheStore.GetString("EmailLabel")}</label>
                        </div>

                        <div className="col-lg-10 col-md-10 col-sm-10">
                            <TextboxMaskedControl
                                isValid={this.state.showPhoneField ? this.IsEmailValid(this.state.emailAddress, true) && this.hasFieldsFilledOut() : this.state.isEmailValid}
                                alphanumericIncludesPeriod={false}
                                id={this.emailFieldName}
                                value={this.state.emailAddress}
                                onChangeFunction={value => this.onEmailChange(value, this.state.showPhoneField)}
                                returnMaskedValue={true}
                                mask={ValidationHelper.GetEmailEditMaskPattern()}
                                validationErrorMessage={cacheStore.GetString("EmailHasInvalidFormatLabel")}
                                isEmail={true}
                                allowEmpty={false}
                            />
                        </div>
                    </div>
                    {this.state.errors?.any() && (
                        <div className="row">
                            <div className="error-message col-sm-10 col-sm-offset-2">
                                <label className="label-error">
                                    {this.state.errors.length > 1
                                        ? <ul>{this.state.errors.map((error, index) => <li key={error + index}>{error}</li>)}</ul>
                                        : this.state.errors.firstOrDefault()
                                    }
                                </label>
                            </div>
                        </div>
                    )}

                    {this.state.showPhoneField && (
                        <div className="row" style={{ "marginBottom": "10px" }}>
                            <div className="col-lg-2 col-md-2 col-sm-2 col-center-align">
                                <label className="form-label" style={{ "paddingTop": "6px" }}>{cacheStore.GetString("PhoneLabel")}</label>
                            </div>

                            <div className="col-lg-10 col-md-10 col-sm-10">
                                <TextboxMaskedControl
                                    id={this.phoneFieldId}
                                    isValid={(this.state.phone.IsValid || !this.state.phone.Number) && this.hasFieldsFilledOut()}
                                    allowEmpty={false}
                                    value={this.state.phone.Number}
                                    onChangeFunction={value => this.onPhoneChanged(value)}
                                    item={this.state.phone}
                                    mask={PhoneNumber.getMask(this.state.phone)}
                                    validationErrorMessage={PhoneNumber.getValidationErrorMessage(this.state.phone)}
                                />
                            </div>
                        </div>
                    )}
                </div>
                <div className="p-footer flex-display flex-end">
                    <ButtonGroup>
                        <ButtonControl
                            variant="neutral"
                            onClick={() => this.props.onCancel()}
                            label={cacheStore.GetString("CancelLabel")}
                        />

                        {this.props.enablePhoneContent && (
                            <ButtonControl
                                variant="brand"
                                onClick={() => this.continueWithoutContact()}
                                label={cacheStore.GetString("ContinueWithoutContactLabel")}
                            />
                        )}

                        <ButtonControl
                            variant="brand"
                            disabled={this.state.showPhoneField ? !this.isFormWithPhoneFieldValid() : this.FormIsInvalid()}
                            onClick={() => this.addNewEmail()}
                            label={cacheStore.GetString("ContinueLabel")}
                        />
                    </ButtonGroup>
                </div>
            </div>
        );
    }

    private IsEmailValid(email: string, isEmptyValid: boolean): boolean {
        return IsNullOrWhiteSpace(email)
            ? isEmptyValid
            : ValidationHelper.isExactMatch(email, ValidationHelper.emailRegex);
    }

    private FormIsInvalid(): boolean {
        return !this.state.isEmailValid
            || IsNullOrWhiteSpace(this.state.emailAddress)
            || (
                !this.state.patientIsOfAgeOfConsent &&
                !this.state.patientHasNoDateOfBirthOnFile &&
                !this.patientHasRepresentant
                && (
                    IsNullOrWhiteSpace(this.state.newRepresentantFirstName)
                    || IsNullOrWhiteSpace(this.state.newRepresentantLastName)
                )
            )
            || this.props.currentlyProcessing;
    }

    private isFormWithPhoneFieldValid(): boolean {
        let isValid = (!this.state.patientIsOfAgeOfConsent && !this.patientHasRepresentant)
            ? this.isNewRepresentativeFormValid()
            : this.isRepresentativeContactFormValid();

        return isValid;
    }

    private isNewRepresentativeFormValid(): boolean {
        return this.hasFieldsFilledOut()
            && this.IsEmailValid(this.state.emailAddress, true)
            && (this.state.phone.IsValid || IsNullOrWhiteSpace(this.state.phone.Number))
            && !IsNullOrWhiteSpace(this.state.newRepresentantFirstName)
            && !IsNullOrWhiteSpace(this.state.newRepresentantLastName)
            && !this.props.currentlyProcessing;
    }

    private isRepresentativeContactFormValid(): boolean {
        return this.hasFieldsFilledOut()
            && this.IsEmailValid(this.state.emailAddress, true)
            && (this.state.phone.IsValid || IsNullOrWhiteSpace(this.state.phone.Number))
            && !this.props.currentlyProcessing;
    }

    private onEmailChange(val: string, isEmptyValid: boolean = false): void {
        this.mutateState(s => {
            s.emailAddress = val;
            s.isEmailValid = this.IsEmailValid(val, isEmptyValid);
        });
    }

    private onPhoneChanged(value: string): void {
        this.mutateState(s => {
            s.phone.Number = value;
            s.phone.IsValid = PhoneNumber.validatePhone(s.phone) || IsNullOrWhiteSpace(value);
        });
    }

    private async addNewEmail(): Promise<void> {
        const result: ClientResponse<AddedContactPointIds> = (!this.patientHasRepresentant && !this.patientIsOfAgeOrHasNoDateOfBirth)
            ? await this.createAndAddNewRepresentant()
            : await this.addContactToSelfOrRepresentant();

        if (result.errors.any()) {
            this.updateErrorCallback(result.errors.map(p => p.Message));
            return;
        }

        this.props.onSubmit([result.data?.EmailContactPointId, result.data?.PhoneContactPointId].filter(p => !!p));
    }

    private async addContactToSelfOrRepresentant(): Promise<ClientResponse<AddedContactPointIds>> {
        return await Client.postAsync<AddedContactPointIds>(
            ServerOperations.Server.Myle_Patient_Representant_AddEmail,
            {
                representantContactId: this.patientIsOfAgeOrHasNoDateOfBirth
                    ? this.state.patient.Contact_id
                    : this.state.representant.contactId,
                emailAddress: this.state.emailAddress,
                phone: this.state.phone,
                allowSMS: this.state.showPhoneField
            }
        );
    }

    private async createAndAddNewRepresentant(): Promise<ClientResponse<AddedContactPointIds>> {
        const relative = new Relative({
            FirstName: this.state.newRepresentantFirstName,
            LastName: this.state.newRepresentantLastName,
            PrimaryPhone: this.state.phone,
            Email: new Email({
                Address: this.state.emailAddress
            })
        });
        relative.PrimaryPhone.NotificationsEnabled = this.state.showPhoneField;
        const representant = await RelativeApiClient.createRelativeAsync(relative);

        const newRelationship = new LinkedContact();
        newRelationship.TargetContactId = representant.ContactId;
        newRelationship.Individual = Person.ToIndividual(representant);
        newRelationship.ContactPoints = representant.ContactPoints;
        newRelationship.Addresses = representant.Addresses;
        newRelationship.PhoneNumbers = representant.Phones;
        newRelationship.Emails = representant.Emails;
        newRelationship.IsRepresentative = true;
        newRelationship.RepresentativeType = this.state.newRepresentantType;
        newRelationship.IsEmergencyContact = false;
        newRelationship.RelationLookupId = this.state.newRelationshipTypeLookupId;
        const model = patientStore.PatientProfileRelationshipsModels[this.state.patient.Contact_id];

        return await Client.postAsync<AddedContactPointIds>(
            ServerOperations.Server.Myle_Patient_Relationships_Update,
            {
                PatientRelationshipsDTO:
                {
                    PatientContactId: this.state.patient.Contact_id,
                    PatientContactFacetId: this.state.patient.ContactFacetId,
                    Relationships: model ? model.get("Relationships") : Immutable.List([newRelationship]),
                    DeleteRelationshipIds: model ? model.get("DeleteRelationshipIds") : Immutable.List([])
                },
                StayInEditMode: false
            }
        );
    }

    private updateErrorCallback(error: string[]): void {
        this.mutateState(s => s.errors = error);
    }

    private setStartingRelationshipType(): void {
        this.mutateState(s => {
            s.newRelationshipTypeLookupId = cacheStore.FindLookupByLookupCode("RLFather").LookupID;
            s.newRepresentantType = "MOP";
        });
    }

    private onSelectRelationshipType(e: React.ChangeEvent<HTMLSelectElement>): void {
        e.persist();
        this.mutateState(s => {
            s.newRelationshipTypeLookupId = cacheStore.FindLookupByLookupCode(e.target.value).LookupID;
            s.newRepresentantType = e.target.value === "RLMother" || e.target.value === "RLFather" ? "MOP" : "ACC";
        });
    }

    private getDisclaimerParagraphs(): string[] {
        if (this.state.patientIsOfAgeOfConsent || this.state.patientHasNoDateOfBirthOnFile) { return [cacheStore.GetString("TeleconsultationRequiresEmailAddressLabel")]; }

        if (this.patientHasRepresentant && !this.state.representant.hasValidEmail) {
            return [
                cacheStore.GetString("PatientUnderAgeToGiveConsentLabel"),
                cacheStore.GetString(this.props.patientWithoutRepresentantContactLabel)
                    .replace("{0}", `${this.state.representant.fullName}`)
            ];
        }

        // patient is underage and has no representant
        return [
            cacheStore.GetString("PatientUnderAgeToGiveConsentLabel"),
            cacheStore.GetString(this.props.patientUnderAgeWithoutRepresentantLabel)
        ];
    }

    private getPatientGender(): Gender {
        return MyleHelper.genderLookupToEnum(this.props.representantsDto.Patient.GenderLookupId);
    }

    private focusFirstField(): void {
        let firstFieldId = !this.patientHasRepresentant && !this.state.patientIsOfAgeOfConsent && !this.state.patientHasNoDateOfBirthOnFile ?
            this.representantFirstNameFieldName : this.emailFieldName;

        document.getElementById(firstFieldId).focus();
    }

    private getRepresentant(linkedContacts: LinkedContact[]): Representant {
        const representants: Representant[] = linkedContacts.map(representant => new Representant(representant));

        const representant = representants
            .filter(representant => representant.hasValidEmail)
            .firstOrDefault();

        return representant != null ? representant : representants.firstOrDefault();
    }

    private hasFieldsFilledOut(): boolean {
        return (!!this.state.emailAddress || !!this.state.phone.Number);
    }

    private continueWithoutContact(): void {
        this.props.onContinueWithoutContact && this.props.onContinueWithoutContact();
    }
}

export { AddRepresentativeEmailPopup, AddRepresentativeEmailPopupProps, AddRepresentativeEmailPopupState };
